import React, { useState } from "react";
import ShowToast from "components/ShowToast";
import { fetchWrapper } from "utils/apiFetch";

// Adds a Cyrus beneficiary via billpay_add_beneficiary.
//
// Shell classes (modal-backdrop-custom / modal-panel / modal-header /
// modal-body / modal-footer / icon-btn / btn outline|primary) are the real
// ones from your working BulkPayoutModal.
//
// The panel itself gets a small set of INLINE styles on top of the class,
// guaranteed regardless of what modal-panel alone provides: a fixed
// max-height with internal scrolling. Without this, a form taller than the
// viewport scrolls the whole page past the (viewport-pinned) backdrop,
// which is exactly the "transparent/scattered fields" bug you saw.
//
// Field rows use inline styles instead of an invented `.form-group` class
// (that class doesn't exist in your real BulkPayoutModal, so it was never
// going to render anything) — guaranteed correct spacing/labeling.
const panelStyle = {
  background: "#fff",
  borderRadius: "12px",
  width: "100%",
  maxWidth: "560px",
  maxHeight: "85vh",
  overflowY: "auto",
  boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
};

const fieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  marginBottom: "14px",
};

const labelStyle = { fontSize: "13px", color: "#666", fontWeight: 500 };

const inputStyle = {
  padding: "10px 12px",
  border: "1px solid #ddd",
  borderRadius: "6px",
  fontSize: "14px",
  width: "100%",
  boxSizing: "border-box",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "18px 22px",
  borderBottom: "1px solid #eee",
  gap: "12px",
};

const headerTitleStyle = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "#1a2b4c",
  flex: "1 1 auto",
  minWidth: 0,
};

const closeBtnStyle = {
  flex: "0 0 auto",
  width: "32px",
  height: "32px",
  minWidth: "32px",
  border: "none",
  background: "#f1f1f1",
  color: "#333",
  borderRadius: "50%",
  fontSize: "18px",
  lineHeight: 1,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const bodyPadStyle = { padding: "18px 22px" };
const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  padding: "16px 22px",
  borderTop: "1px solid #eee",
};

const fieldsetStyle = {
  border: "1px solid #eee",
  borderRadius: "8px",
  padding: "14px",
  marginBottom: "14px",
};

const AddBillpayBankAccountModal = ({ show, onClose, onSuccess }) => {
  const initialState = {
    beneficiary_name: "",
    beneficiary_phone: "",
    beneficiary_email: "",
    beneficiary_bank_account_number: "",
    beneficiary_bank_ifsc_code: "",
    beneficiary_aadhar: "",
    pan_number: "",
    bene_type: "EMPLOYEE",
    addr_line: "",
    addr_area: "",
    addr_city: "",
    addr_district: "",
    addr_state: "",
    addr_pincode: "",
    aadhar_checked: false,
    consent: false,
  };

  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  if (!show) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const resetAndClose = () => {
    setForm(initialState);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.beneficiary_name || !form.beneficiary_bank_account_number || !form.beneficiary_bank_ifsc_code) {
      ShowToast("Name, account number and IFSC are required", "warning");
      return;
    }
    if (!form.consent) {
      ShowToast("Beneficiary consent is required", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const beneficiary_address = JSON.stringify({
        line: form.addr_line,
        area: form.addr_area,
        city: form.addr_city,
        district: form.addr_district,
        state: form.addr_state,
        pincode: form.addr_pincode,
      });

      let latlong = "";
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation
            ? navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
            : reject(new Error("no geolocation"))
        );
        latlong = `${pos.coords.latitude},${pos.coords.longitude}`;
      } catch {
        latlong = "0,0";
      }

      const formData = new FormData();
      formData.append("pay_type", "account_number");
      formData.append("beneficiary_bank_account_number", form.beneficiary_bank_account_number);
      formData.append("beneficiary_bank_ifsc_code", form.beneficiary_bank_ifsc_code);
      formData.append("beneficiary_name", form.beneficiary_name);
      formData.append("beneficiary_email", form.beneficiary_email);
      formData.append("beneficiary_phone", form.beneficiary_phone);
      formData.append("beneficiary_aadhar", form.beneficiary_aadhar);
      formData.append("beneficiary_address", beneficiary_address);
      formData.append("bene_type", form.bene_type);
      formData.append("latlong", latlong);
      formData.append("Aadhar_checked_and_verified", form.aadhar_checked ? "true" : "false");
      formData.append("consent", form.consent ? "true" : "false");
      formData.append("pan_number", form.pan_number);

      const res = await fetchWrapper("https://pro.mokshiq.in/billpay_add_beneficiary", {
        method: "POST",
        body: formData,
      });

      if (res?.status) {
        ShowToast(res.data || "Beneficiary added successfully", "success");
        onSuccess && onSuccess();
        resetAndClose();
      } else {
        ShowToast(res?.data || "Failed to add beneficiary", "danger");
      }
    } catch (e) {
      ShowToast("Something went wrong while adding the beneficiary", "danger");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop-custom" role="dialog" aria-modal="true">
      <div className="modal-panel" style={panelStyle}>
        <div className="modal-header" style={headerStyle}>
          <h5 style={headerTitleStyle}>Add Bill Pay Account</h5>
          <button type="button" style={closeBtnStyle} onClick={resetAndClose} aria-label="Close">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={bodyPadStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Account Holder Name *</label>
              <input style={inputStyle} name="beneficiary_name" value={form.beneficiary_name} onChange={handleChange} required />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Mobile Number</label>
              <input style={inputStyle} name="beneficiary_phone" value={form.beneficiary_phone} onChange={handleChange} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} name="beneficiary_email" value={form.beneficiary_email} onChange={handleChange} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Account Number *</label>
              <input
                style={inputStyle}
                name="beneficiary_bank_account_number"
                value={form.beneficiary_bank_account_number}
                onChange={handleChange}
                required
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>IFSC Code *</label>
              <input
                style={inputStyle}
                name="beneficiary_bank_ifsc_code"
                value={form.beneficiary_bank_ifsc_code}
                onChange={handleChange}
                required
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Aadhar Number</label>
              <input style={inputStyle} name="beneficiary_aadhar" value={form.beneficiary_aadhar} onChange={handleChange} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>PAN Number</label>
              <input style={inputStyle} name="pan_number" value={form.pan_number} onChange={handleChange} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Beneficiary Type</label>
              <select style={inputStyle} name="bene_type" value={form.bene_type} onChange={handleChange}>
                <option value="EMPLOYEE">Employee</option>
                <option value="AGENT">Agent</option>
                <option value="CUSTOMER">Customer</option>
                <option value="VENDOR">Vendor</option>
              </select>
            </div>

            <fieldset style={fieldsetStyle}>
              <legend style={{ fontSize: "13px", color: "#666", padding: "0 6px" }}>Address</legend>
              <div style={fieldStyle}>
                <label style={labelStyle}>Line</label>
                <input style={inputStyle} name="addr_line" value={form.addr_line} onChange={handleChange} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Area</label>
                <input style={inputStyle} name="addr_area" value={form.addr_area} onChange={handleChange} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>City</label>
                <input style={inputStyle} name="addr_city" value={form.addr_city} onChange={handleChange} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>District</label>
                <input style={inputStyle} name="addr_district" value={form.addr_district} onChange={handleChange} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>State</label>
                <input style={inputStyle} name="addr_state" value={form.addr_state} onChange={handleChange} />
              </div>
              <div style={{ ...fieldStyle, marginBottom: 0 }}>
                <label style={labelStyle}>Pincode</label>
                <input style={inputStyle} name="addr_pincode" value={form.addr_pincode} onChange={handleChange} />
              </div>
            </fieldset>

            <div style={{ ...fieldStyle, flexDirection: "row", alignItems: "center", gap: "8px" }}>
              <input type="checkbox" name="aadhar_checked" checked={form.aadhar_checked} onChange={handleChange} />
              <label style={{ fontSize: "14px" }}>Aadhar checked and verified</label>
            </div>
            <div style={{ ...fieldStyle, flexDirection: "row", alignItems: "center", gap: "8px" }}>
              <input type="checkbox" name="consent" checked={form.consent} onChange={handleChange} />
              <label style={{ fontSize: "14px" }}>Beneficiary consent obtained *</label>
            </div>
          </div>

          <div className="modal-footer" style={footerStyle}>
            <button type="button" className="btn outline" onClick={resetAndClose} disabled={submitting}>
              Close
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting ? "Adding..." : "Add Beneficiary"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBillpayBankAccountModal;